import { EmbeddableDatasourceSubplaceWithoutRouter } from '@tableau/data-catalog-client-components';
import { NaturalLanguageDatasourceOwnerPanel } from '@tableau/natural-language-datasource-owner-panel';
import * as React from 'react';
import { defaultMemoize } from 'reselect';
import * as _ from 'underscore';
import { catalogApolloClient } from '../../../catalog/CatalogApolloClient';
import { Messages } from '../../../codegen/Localize';
import { NaturalLanguage } from '../../../common/react/components/NaturalLanguage';
import { ReactDirectiveProps } from '../../../ts/directives/reactDirectiveFactory';
import { AngularContext } from '../../../ts/factories/AngularContextFactory';
import {
  ConnectedWorkbooksInDatasourceDefinition,
  DataConnectionDefinition,
  ExtractTaskDefinition,
  RemoteRefreshSchedulesDefinition,
} from '../../../ts/models/ContentDefinitions';
import { FlowListItemInfo } from '../../../ts/models/FlowInfo';
import { IDataSourceDetailInfoWithUsage } from '../../../ts/models/IDataSourceInfo';
import { Flows } from '../../../ts/resources/Flows';
import { IDatasourceActions } from '../../../ts/ServerApi/types/IDatasourceActions';
import { RefreshModeTypes } from '../../../ts/ServerApi/types/IRemoteRefreshProperties';
import { DataCatalogApiRequestsService } from '../../../ts/services/DataCatalogApiRequestsService';
import { DataCatalogUIActionsService } from '../../../ts/services/DataCatalogUIActionsService';
import { Telemetry } from '../../../ts/services/TelemetryService';
import { ServiceLocator } from '../../ServiceLocator';
import { ISpaceInfoTypeItemCount } from '../../state/ContentCountsState';
import { DatasourceSubspaceName, SpaceInfo } from '../../state/SpaceInfo';
import { SpacesSiteUrls } from '../SpacesSiteUrls';
import { maybeSendTelemetry_favoriteItemOpened } from '../SpaceTelemetry';
import {
  explorerSubspace,
  newExtractRefreshButton,
  newRemoteExtractRefreshButton,
  remoteExtractRefreshPropertiesButton
} from '../SubspaceHelpers';
import { Subspace, TabbedSpace } from '../TabbedSpace';
import { DatasourceSpaceHeader } from './DatasourceSpaceHeader';

export interface DatasourceSpaceStateProps {
  readonly flowsOnSiteEnabled: boolean;
  readonly catalogingOnSiteEnabled: boolean;
  readonly spaceInfo: SpaceInfo;
  readonly urls: SpacesSiteUrls;
  readonly contentCounts: ISpaceInfoTypeItemCount<'datasource'>;
  readonly contentCountsUptoDate: boolean;
  readonly siteId: string;
  readonly siteUrlName: string;
  readonly areUsersVisible: boolean;
}

export interface DatasourceSpaceDispatchProps {
  readonly fetchUpdatedContentCounts: () => void;
}

export interface DatasourceSpaceOwnProps extends ReactDirectiveProps {
  readonly datasourceDetail: IDataSourceDetailInfoWithUsage;
  readonly datasourceActions: IDatasourceActions;
  readonly openDetail: () => void;
  readonly addToFavorites: () => void;
  readonly removeFromFavorites: () => void;
  readonly sanitizer: ng.sanitize.ISanitizeService;
  readonly telemetry: Telemetry;
  readonly showNLPSubspaces: boolean;
  readonly bridgeEnabled: boolean;
  readonly dataCatalogApiRequestsService: DataCatalogApiRequestsService;
}

export type DatasourceSpaceProps = DatasourceSpaceStateProps & DatasourceSpaceDispatchProps & DatasourceSpaceOwnProps;

export const getDatasourceUrl = (urls: SpacesSiteUrls, datasourceId: string, subspace?: DatasourceSubspaceName) => {
  if (subspace) {
    const DatasourceSubspacesToUrl: Record<DatasourceSubspaceName, string> = {
      nlp: urls.datasourceAskDataUrl(datasourceId),
      connections: urls.datasourceConnectionsUrl(datasourceId),
      extractRefreshes: urls.datasourceExtractRefreshesUrl(datasourceId),
      remoteRefreshSchedules: urls.datasourceLocalExtractRefreshesUrl(datasourceId),
      connectedWorkbooks: urls.datasourceConnectedWorkbooksUrl(datasourceId),
      askDataUsageAnalytics: urls.datasourceAskDataUsageAnalyticsUrl(datasourceId),
      lineage: urls.datasourceLineageUrl(datasourceId)
    };

    const url = DatasourceSubspacesToUrl[subspace];
    if (url) {
      return url;
    }
  }

  return urls.datasourceUrl(datasourceId);
};

function nlpSubspace(dataSourceUrlName: string, siteUrlName: string): Subspace {
  return {
    name: 'nlp',
    tabLabel: Messages.nlpTab(),
    shouldRenderTabContentCount: false,
    renderContent: () => {
      return (
        <NaturalLanguage
          dataSourceUrlName={dataSourceUrlName}
          siteUrlName={siteUrlName}
        />
      );
    }
  };
}

function lineageSubspace(
  datasource: IDataSourceDetailInfoWithUsage,
  siteUrls: SpacesSiteUrls,
  dataCatalogApiRequestsService: DataCatalogApiRequestsService,
  areUsersVisible: boolean
): Subspace {
  return {
    name: 'lineage',
    tabLabel: Messages.dataCatalogLineage(),
    shouldRenderTabContentCount: false,
    renderContent: () =>
      <EmbeddableDatasourceSubplaceWithoutRouter
        vizportalUrlId={datasource.id}
        client={catalogApolloClient}
        siteUrls={siteUrls}
        apiRequests={dataCatalogApiRequestsService}
        uiActions={DataCatalogUIActionsService.instance()}
        areUsersVisible={areUsersVisible}
      />
  };
}

export function getDatasourceSubspaces(
  angularContext: AngularContext,
  telemetry: Telemetry,
  showNLPTab: boolean,
  bridgeEnabled: boolean,
  siteUrlName: string,
  siteId: string,
  datasource: IDataSourceDetailInfoWithUsage,
  datasourceActions: IDatasourceActions,
  contentCounts: ISpaceInfoTypeItemCount<'datasource'>,
  contentCountsUpToDate: boolean,
  siteUrls: SpacesSiteUrls,
  catalogingOnSiteEnabled: boolean,
  dataCatalogApiRequestsService: DataCatalogApiRequestsService,
  areUsersVisible: boolean
): ReadonlyArray<Subspace> {
  const showRemoteExtractRefreshes = bridgeEnabled &&
      datasource.hasExtracts &&
      datasource.refreshMode === RefreshModeTypes.Remote;
  const showExtractRefreshes = datasource.hasExtracts && !showRemoteExtractRefreshes;

  return _.compact([
    showNLPTab && nlpSubspace(datasource.urlName, siteUrlName),
    explorerSubspace({
      name: 'connections',
      tabLabel: Messages.connections(),
      supportedDisplayModes: DataConnectionDefinition.supportedDisplayModes,
      angularContext: angularContext,
      telemetry: telemetry,
      shouldRenderContentCount: true,
      contentCount: contentCounts && contentCounts['connections'],
      contentCountUpToDate: contentCountsUpToDate,
      toolbarVisibility: 'enabled'
    }),
    showExtractRefreshes && explorerSubspace({  // corresponds to /extractRefreshes url
      name: 'extractRefreshes',
      tabLabel: Messages.extractTasks(),
      supportedDisplayModes: ExtractTaskDefinition.supportedDisplayModes,
      angularContext: angularContext,
      telemetry: telemetry,
      toolbarContentSpecificControls: {
        beginning: newExtractRefreshButton(datasource, datasourceActions),
        end: bridgeEnabled && remoteExtractRefreshPropertiesButton(datasource, datasourceActions),
      },
      shouldRenderContentCount: true,
      contentCount: contentCounts && contentCounts['extractRefreshes'],
      contentCountUpToDate: contentCountsUpToDate,
      toolbarVisibility: 'enabled'
    }),
    showRemoteExtractRefreshes && explorerSubspace({  // corresponds to /localExtractRefreshes url
      name: 'remoteRefreshSchedules',
      tabLabel: Messages.extractTasks(),
      supportedDisplayModes: RemoteRefreshSchedulesDefinition.supportedDisplayModes,
      angularContext: angularContext,
      telemetry: telemetry,
      toolbarContentSpecificControls: {
        beginning: newRemoteExtractRefreshButton(datasource),
        end: remoteExtractRefreshPropertiesButton(datasource, datasourceActions),
      },
      shouldRenderContentCount: true,
      contentCount: contentCounts && contentCounts['remoteRefreshSchedules'],
      contentCountUpToDate: contentCountsUpToDate,
      toolbarVisibility: 'enabled'
    }),
    explorerSubspace({
      name: 'connectedWorkbooks',
      tabLabel: Messages.connectedWorkbooks(),
      supportedDisplayModes: ConnectedWorkbooksInDatasourceDefinition.supportedDisplayModes,
      angularContext: angularContext,
      telemetry: telemetry,
      shouldRenderContentCount: true,
      contentCount: contentCounts && contentCounts['connectedWorkbooks'],
      contentCountUpToDate: contentCountsUpToDate,
      toolbarVisibility: 'enabled'
    }),
    catalogingOnSiteEnabled && lineageSubspace(
      datasource,
      siteUrls,
      dataCatalogApiRequestsService,
      areUsersVisible
    )
  ]);
}

type DatasourceSpaceState = {
  readonly flows: ReadonlyArray<FlowListItemInfo>;
};

export class DatasourceSpace extends React.PureComponent<DatasourceSpaceProps, DatasourceSpaceState> {

  private readonly memoizedGetDatasourceSubspaces = defaultMemoize(getDatasourceSubspaces);
  private unmounted: boolean = false;

  constructor(props: DatasourceSpaceProps) {
    super(props);
    this.state = {
      flows: [],
    };
    this.fetchFlows();
  }

  public exploreDatasourceUrl = (subspaceName?: DatasourceSubspaceName)  => {
    return getDatasourceUrl(this.props.urls, this.props.datasourceDetail.id, subspaceName);
  }

  public render() {
    const subspaces = this.memoizedGetDatasourceSubspaces(
      this.props.angularContext,
      this.props.telemetry,
      this.props.showNLPSubspaces,
      this.props.bridgeEnabled,
      this.props.siteUrlName,
      this.props.siteId,
      this.props.datasourceDetail,
      this.props.datasourceActions,
      this.props.contentCounts,
      this.props.contentCountsUptoDate,
      this.props.urls,
      this.props.catalogingOnSiteEnabled,
      this.props.dataCatalogApiRequestsService,
      this.props.areUsersVisible
    );

    if(this.props.spaceInfo.subspaceName === 'askDataUsageAnalytics' && this.props.showNLPSubspaces) {
      return <NaturalLanguageDatasourceOwnerPanel
        unifiedDatasourceId={this.props.datasourceDetail.idForFavoriting}
        datasourceName={this.props.datasourceDetail.name}
        siteUrlName={this.props.siteUrlName}
      />;
    }

    return (
      <TabbedSpace
        subspaces={subspaces}
        activeSubspaceName={this.props.spaceInfo.subspaceName}
        urlForSubspace={this.exploreDatasourceUrl}
        mastheadElement={this.mastheadElement()}
        testIdPrefix={'datasource'}
        spaceItemName={this.props.datasourceDetail.name}
      />
    );
  }

  public componentDidUpdate(prevProps: DatasourceSpaceProps) {
    if (this.props.datasourceDetail.id !== prevProps.datasourceDetail.id) {
      this.fetchFlows();
      this.maybeSendTelemetryEvent();
    }
    this.fetchUpdatedCounts();
  }

  public componentDidMount(): void {
    this.fetchUpdatedCounts();
    this.maybeSendTelemetryEvent();
  }

  private maybeSendTelemetryEvent() {
    // Use the "ID for favoriting" for data sources so it matches the IDs coming from the favorites menu telemetry.
    // When unified data source IDs are fixed, this can use the datasource's normal ID.
    const datasourceForTelemetry = {
      ...this.props.datasourceDetail,
      id: this.props.datasourceDetail.idForFavoriting,
    };
    maybeSendTelemetry_favoriteItemOpened(this.props.telemetry, datasourceForTelemetry);
  }

  public componentWillUnmount() {
    this.unmounted = true;
  }

  private fetchFlows() {
    if (this.props.flowsOnSiteEnabled) {
      const flows: Flows = ServiceLocator.get('Flows');
      flows.getFlowsByDatasourceId(this.props.datasourceDetail.id).then(this.updateFlows);
    }
  }

  private readonly updateFlows = (flows: ReadonlyArray<FlowListItemInfo>) => {
    if (!this.unmounted) {
      if (flows && flows.length > 0) {
        this.setState({ flows });
      }
    }
  }

  private mastheadElement(): React.ReactNode {
    return <DatasourceSpaceHeader
      item={this.props.datasourceDetail}
      availableActions={this.props.datasourceActions}
      flows={this.state.flows}
      openDetail={this.props.openDetail}
      addToFavorites={this.props.addToFavorites}
      removeFromFavorites={this.props.removeFromFavorites}
      sanitizer={this.props.sanitizer}
      urls={this.props.urls}
      catalogingOnSiteEnabled={this.props.catalogingOnSiteEnabled}
      apiRequests={this.props.dataCatalogApiRequestsService}
    />;
  }

  private fetchUpdatedCounts = (): void => {
    if (this.props.fetchUpdatedContentCounts) {
      this.props.fetchUpdatedContentCounts();
    }
  }
}
